/**
 * Mapa SVG interactivo: dibujo, estados visuales, zoom/desplazamiento (rueda con Ctrl/⌘, arrastre, pellizco,
 * doble toque, botones), selección por puntero con "zona táctil" ampliada y navegación con teclado.
 *
 * Un solo conjunto de listeners (delegación) sobre el visor: no hay un listener por butaca.
 * Cada butaca es un único <circle>; los símbolos (✓, ×, ···, /, accesible) viven en una capa aparte y solo
 * existen para las butacas que los necesitan.
 */
import type { ChartModel } from "./chart-model.js";
import { BLOCKED, FREE, HELD, MINE, SOLD } from "./availability.js";
import { h, markPath, round1, s, type MarkKind } from "./dom.js";
import {
  centerOn,
  clampView,
  Grid,
  kForPitch,
  makeFrame,
  navigate,
  panBy,
  pinchView,
  scaleOf,
  toScreen,
  toWorld,
  transformOf,
  wheelFactor,
  zoomAt,
  type Dir,
  type Frame,
  type NavData,
  type Pt,
  type View,
} from "./geometry.js";

export interface MapCallbacks {
  /** node: 0..n-1 butaca; n.. zona */
  activate(node: number, via: "pointer" | "key" | "ax"): void;
  hover(node: number, x: number, y: number): void;
  focus(node: number, byKeyboard: boolean): void;
  blur(): void;
  /** devuelve true si cerró algo */
  escape(): boolean;
  /** tap en una zona vacía del mapa */
  backgroundTap(): void;
  viewChange(): void;
  wheelHint(): void;
}

export interface MapOptions {
  model: ChartModel;
  seatLabel(i: number): string;
  zoneLabel(zi: number): string;
  label: string;
  describedBy: string;
  reduceMotion(): boolean;
  cb: MapCallbacks;
}

export interface ZonePaint {
  cls: "free" | "mine" | "out";
  sub: string;
  qty: number;
  label: string;
}

const CLS = ["free", "sold", "held", "blocked", "mine"];
const KEY_DIR: Record<string, Dir> = { ArrowLeft: "left", ArrowRight: "right", ArrowUp: "up", ArrowDown: "down" };
const TAP_SLOP = 7;
const DBL_MS = 320;
/** por debajo de esta separación en pantalla, un toque con el dedo acerca en vez de elegir */
const MIN_TOUCH_PITCH = 17;
const COMFORT_PITCH = 32;

export class MapView {
  readonly el: HTMLDivElement;
  readonly seatCount: number;
  frame: Frame;
  view: View;
  private readonly m: ChartModel;
  private readonly svg: SVGSVGElement;
  private readonly world: SVGGElement;
  private readonly seatEls: SVGCircleElement[] = [];
  private readonly zoneEls: SVGGElement[] = [];
  private readonly zoneParts: Array<{ sub: SVGTextElement; badge: SVGGElement; badgeText: SVGTextElement }> = [];
  private readonly rowEls: SVGGElement[] = [];
  private readonly marks = new Map<number, SVGPathElement>();
  private readonly marksG: SVGGElement;
  private readonly ring1: SVGRectElement;
  private readonly ring2: SVGRectElement;
  private readonly grid: Grid;
  private readonly nav: NavData;
  private readonly pointers = new Map<number, Pt>();
  private down: { id: number; x: number; y: number; view: View; moved: boolean; type: string } | null = null;
  private pinch: { a0: Pt; b0: Pt; view: View } | null = null;
  private lastTap: { t: number; x: number; y: number } | null = null;
  private lastHover = -2;
  private hoverRaf = 0;
  private hoverPt: Pt | null = null;
  private anim = 0;
  private current = -1;
  private kbd = false;
  private lastPointerUp = 0;
  private w = 0;
  private hh = 0;

  constructor(private readonly o: MapOptions) {
    const m = (this.m = o.model);
    this.seatCount = m.seats.length;
    this.frame = makeFrame(400, 300, m.width, m.height, m.pitch);
    this.view = { k: 1, cx: m.width / 2, cy: m.height / 2 };

    this.world = s("g", { class: "world" });
    const deco = s("g", { "aria-hidden": "true" });
    if (m.stage) {
      const st = m.stage;
      deco.appendChild(s("rect", { class: "stage", x: st.x, y: st.y, width: st.w, height: st.h, rx: Math.min(14, st.h / 2) }));
      const t = s("text", { class: "stl", x: round1(st.x + st.w / 2), y: round1(st.y + st.h / 2), "font-size": round1(Math.min(st.h * 0.3, m.pitch * 0.75)) });
      t.textContent = st.label;
      deco.appendChild(t);
    }
    for (const sec of m.sections) {
      if (!sec.label) continue;
      const t = s("text", { class: "sec", x: sec.x, y: sec.y, "font-size": round1(m.pitch * 0.58) });
      t.textContent = sec.label;
      deco.appendChild(t);
    }
    this.world.appendChild(deco);

    // Zonas de entrada general
    const zonesG = s("g");
    m.zones.forEach((z, zi) => {
      const cat = m.cats[z.cat]!;
      const fs = Math.max(8, Math.min(18, z.h * 0.3));
      const g = s("g", { class: "z free", "data-n": this.seatCount + zi, role: "button", "aria-haspopup": "dialog", "aria-label": o.zoneLabel(zi) });
      g.style.setProperty("--c", cat.color);
      g.appendChild(s("rect", { class: "zb", x: z.x, y: z.y, width: z.w, height: z.h, rx: 9 }));
      const l = s("text", { class: "zl", x: round1(z.x + z.w / 2), y: round1(z.y + z.h / 2 - fs * 0.62), "font-size": round1(fs) });
      l.textContent = z.label;
      const sub = s("text", { class: "zs", x: round1(z.x + z.w / 2), y: round1(z.y + z.h / 2 + fs * 0.72), "font-size": round1(fs * 0.82) });
      const bw = fs * 2.5;
      const bh = fs * 1.5;
      const badge = s("g", { class: "zq", display: "none" });
      badge.appendChild(s("rect", { x: round1(z.x + z.w - bw - 6), y: round1(z.y + 6), width: round1(bw), height: round1(bh), rx: round1(bh / 2) }));
      const bt = s("text", { x: round1(z.x + z.w - bw / 2 - 6), y: round1(z.y + 6 + bh / 2), "font-size": round1(fs * 0.9) });
      badge.appendChild(bt);
      g.append(l, sub, badge);
      zonesG.appendChild(g);
      this.zoneEls.push(g);
      this.zoneParts.push({ sub, badge, badgeText: bt });
    });
    this.world.appendChild(zonesG);

    // Butacas, agrupadas por fila (el grupo lleva el color de su categoría)
    const seatsG = s("g");
    const tpl = s("circle", { class: "s free", r: round1(m.radius), role: "checkbox", "aria-checked": "false" });
    const frag = document.createDocumentFragment();
    m.rows.forEach((row) => {
      const g = s("g", { class: "row", "data-cat": row.cat });
      g.style.setProperty("--c", m.cats[row.cat]!.color);
      for (const i of row.seats) {
        const seat = m.seats[i]!;
        const c = tpl.cloneNode(false) as SVGCircleElement;
        c.setAttribute("cx", String(seat.x));
        c.setAttribute("cy", String(seat.y));
        c.setAttribute("data-n", String(i));
        c.setAttribute("aria-label", o.seatLabel(i));
        g.appendChild(c);
        this.seatEls[i] = c;
      }
      this.rowEls.push(g);
      frag.appendChild(g);
    });
    seatsG.appendChild(frag);
    this.world.appendChild(seatsG);

    this.marksG = s("g", { "aria-hidden": "true" });
    this.world.appendChild(this.marksG);
    this.ring1 = s("rect", { class: "fr1", display: "none" });
    this.ring2 = s("rect", { class: "fr2", display: "none" });
    this.world.append(this.ring1, this.ring2);

    this.svg = s("svg", { class: "sv", role: "group", "aria-label": o.label, "aria-describedby": o.describedBy });
    this.svg.appendChild(this.world);
    this.el = h("div", { class: "vp" }, this.svg);

    for (let i = 0; i < m.seats.length; i++) if (m.seats[i]!.accessible) this.setMark(i, "access");

    // Datos para localizar y navegar
    const total = m.seats.length + m.zones.length;
    const xs = new Float64Array(total);
    const ys = new Float64Array(total);
    const rowOf = new Int32Array(total).fill(-1);
    const posOf = new Int32Array(total);
    m.seats.forEach((st, i) => {
      xs[i] = st.x;
      ys[i] = st.y;
      rowOf[i] = st.row;
      posOf[i] = st.pos;
    });
    m.zones.forEach((z, zi) => {
      xs[m.seats.length + zi] = z.x + z.w / 2;
      ys[m.seats.length + zi] = z.y + z.h / 2;
    });
    this.nav = { xs, ys, rowOf, posOf, rowNodes: m.rows.map((r) => r.seats) };
    this.grid = new Grid(xs.subarray(0, m.seats.length), ys.subarray(0, m.seats.length), Math.max(1, m.pitch));

    this.bind();
    this.applyView();
  }

  /* ------------------------------ pintado --------------------------------- */

  paintSeat(i: number, vis: number, pending: boolean, label: string): void {
    const e = this.seatEls[i];
    if (!e) return;
    e.setAttribute("class", `s ${CLS[vis]}${vis === MINE && pending ? " pend" : ""}`);
    e.setAttribute("aria-label", label);
    e.setAttribute("aria-checked", vis === MINE ? "true" : "false");
    if (vis === SOLD || vis === HELD || vis === BLOCKED) e.setAttribute("aria-disabled", "true");
    else e.removeAttribute("aria-disabled");
    const kind: MarkKind | null = vis === MINE ? "check" : vis === SOLD ? "cross" : vis === HELD ? "dots" : vis === BLOCKED ? "slash" : this.m.seats[i]!.accessible ? "access" : null;
    this.setMark(i, kind);
    if (this.current === i && !this.ring1.hasAttribute("display")) this.placeRing(i);
  }

  private setMark(i: number, kind: MarkKind | null): void {
    const prev = this.marks.get(i);
    if (!kind) {
      if (prev) {
        prev.remove();
        this.marks.delete(i);
      }
      return;
    }
    const seat = this.m.seats[i]!;
    const r = this.m.radius;
    const d = markPath(kind, seat.x, seat.y, r);
    if (prev) {
      prev.setAttribute("d", d);
      prev.setAttribute("class", `mk ${kind}`);
      prev.style.setProperty("--ink", this.m.cats[seat.cat]!.ink);
      prev.setAttribute("stroke-width", String(round1(kind === "dots" ? r * 0.36 : r * 0.24)));
      return;
    }
    const p = s("path", { class: `mk ${kind}`, d, "stroke-width": round1(kind === "dots" ? r * 0.36 : r * 0.24) });
    p.style.setProperty("--ink", this.m.cats[seat.cat]!.ink);
    this.marksG.appendChild(p);
    this.marks.set(i, p);
  }

  paintZone(zi: number, p: ZonePaint): void {
    const g = this.zoneEls[zi];
    if (!g) return;
    g.setAttribute("class", `z ${p.cls}`);
    g.setAttribute("aria-label", p.label);
    if (p.cls === "out") g.setAttribute("aria-disabled", "true");
    else g.removeAttribute("aria-disabled");
    const parts = this.zoneParts[zi]!;
    parts.sub.textContent = p.sub;
    if (p.qty > 0) {
      parts.badge.removeAttribute("display");
      parts.badgeText.textContent = `✓ ${p.qty}`;
    } else parts.badge.setAttribute("display", "none");
  }

  /** Resalta una categoría (atenúa el resto). -1 quita el resalte. */
  highlight(cat: number): void {
    this.el.parentElement?.toggleAttribute("data-hl", cat >= 0);
    this.rowEls.forEach((g) => g.classList.toggle("on", Number(g.getAttribute("data-cat")) === cat));
    this.m.zones.forEach((z, zi) => this.zoneEls[zi]!.classList.toggle("on", z.cat === cat));
  }

  /* ------------------------------ tamaño y vista -------------------------- */

  /** Ajusta el visor. La altura sigue la proporción del plano (con tope) y la vista se conserva. */
  resize(width: number): number {
    const ratio = this.m.height / this.m.width;
    const maxH = Math.max(300, Math.min(typeof window !== "undefined" ? window.innerHeight * 0.8 : 700, 760));
    const height = Math.round(Math.max(Math.min(width * ratio, maxH), width < 520 ? 260 : 300));
    this.w = width;
    this.hh = height;
    this.el.style.height = `${height}px`;
    this.frame = makeFrame(width, height, this.m.width, this.m.height, this.m.pitch);
    this.view = clampView(this.frame, this.view);
    this.applyView();
    return height;
  }

  private applyView(): void {
    const t = transformOf(this.frame, this.view);
    this.world.setAttribute("transform", `translate(${round1(t.tx * 100) / 100} ${round1(t.ty * 100) / 100}) scale(${Math.round(t.s * 10000) / 10000})`);
    const zoomed = this.view.k > 1.02;
    this.el.classList.toggle("z", zoomed);
    this.o.cb.viewChange();
  }

  setView(v: View, cancel = true): void {
    if (cancel) cancelAnimationFrame(this.anim);
    this.view = clampView(this.frame, v);
    this.applyView();
  }

  animateTo(target: View): void {
    cancelAnimationFrame(this.anim);
    const t = clampView(this.frame, target);
    if (this.o.reduceMotion()) return this.setView(t);
    const from = this.view;
    const t0 = performance.now();
    const step = (now: number) => {
      const u = Math.min(1, (now - t0) / 220);
      const e = 1 - (1 - u) ** 3;
      this.setView({ k: from.k + (t.k - from.k) * e, cx: from.cx + (t.cx - from.cx) * e, cy: from.cy + (t.cy - from.cy) * e }, false);
      if (u < 1) this.anim = requestAnimationFrame(step);
    };
    this.anim = requestAnimationFrame(step);
  }

  zoomBy(factor: number, at?: Pt): void {
    const p = at ?? { x: this.w / 2, y: this.hh / 2 };
    this.animateTo(zoomAt(this.frame, this.view, p.x, p.y, this.view.k * factor));
  }

  reset(): void {
    this.animateTo({ k: 1, cx: this.m.width / 2, cy: this.m.height / 2 });
  }

  get zoomed(): boolean {
    return this.view.k > 1.02;
  }
  get atMax(): boolean {
    return this.view.k >= this.frame.kMax - 0.01;
  }

  /* ------------------------------ nodos ----------------------------------- */

  /** Posición en pantalla (px dentro del visor) del centro de un nodo. */
  nodePos(n: number): Pt {
    return toScreen(this.frame, this.view, this.nav.xs[n]!, this.nav.ys[n]!);
  }

  /** Radio aproximado de un nodo en pantalla. */
  nodeRadius(n: number): number {
    const sc = scaleOf(this.frame, this.view);
    if (n < this.seatCount) return this.m.radius * sc;
    const z = this.m.zones[n - this.seatCount]!;
    return (z.h / 2) * sc;
  }

  private nodeEl(n: number): SVGElement | undefined {
    return n < this.seatCount ? this.seatEls[n] : this.zoneEls[n - this.seatCount];
  }

  private nodeOf(target: EventTarget | null): number {
    const el = (target as Element | null)?.closest?.("[data-n]");
    if (!el) return -1;
    const n = Number(el.getAttribute("data-n"));
    return Number.isInteger(n) ? n : -1;
  }

  /** Nodo tabulable (roving tabindex): solo uno de los elementos del mapa está en el orden de tabulación. */
  setCurrent(n: number): void {
    if (n === this.current || n < 0) return;
    if (this.current >= 0) this.nodeEl(this.current)?.removeAttribute("tabindex");
    this.current = n;
    this.nodeEl(n)?.setAttribute("tabindex", "0");
  }
  get currentNode(): number {
    return this.current;
  }

  focusNode(n: number, byKeyboard: boolean, reveal = true): void {
    const e = this.nodeEl(n);
    if (!e) return;
    this.kbd = byKeyboard;
    if (reveal) this.ensureVisible(n);
    this.setCurrent(n);
    e.focus({ preventScroll: true });
  }

  private ensureVisible(n: number): void {
    const p = this.nodePos(n);
    const margin = 36;
    if (p.x < margin || p.y < margin || p.x > this.w - margin || p.y > this.hh - margin) {
      this.animateTo(centerOn(this.frame, this.nav.xs[n]!, this.nav.ys[n]!, this.view.k));
    }
  }

  private placeRing(n: number): void {
    const sc = scaleOf(this.frame, this.view);
    let x: number, y: number, w: number, hgt: number, rx: number;
    if (n < this.seatCount) {
      const sd = this.m.seats[n]!;
      const r = this.m.radius + 2.5 / sc;
      x = sd.x - r;
      y = sd.y - r;
      w = hgt = 2 * r;
      rx = r;
    } else {
      const z = this.m.zones[n - this.seatCount]!;
      const pad = 3 / sc;
      x = z.x - pad;
      y = z.y - pad;
      w = z.w + 2 * pad;
      hgt = z.h + 2 * pad;
      rx = 9 + pad;
    }
    for (const r of [this.ring1, this.ring2]) {
      r.setAttribute("x", String(round1(x)));
      r.setAttribute("y", String(round1(y)));
      r.setAttribute("width", String(round1(w)));
      r.setAttribute("height", String(round1(hgt)));
      r.setAttribute("rx", String(round1(rx)));
      r.removeAttribute("display");
    }
  }

  private hideRing(): void {
    this.ring1.setAttribute("display", "none");
    this.ring2.setAttribute("display", "none");
  }

  /** Primer nodo disponible para la tabulación inicial. */
  firstNode(isFree: (i: number) => boolean): number {
    for (let i = 0; i < this.seatCount; i++) if (isFree(i)) return i;
    return this.seatCount > 0 ? 0 : this.m.zones.length ? this.seatCount : -1;
  }

  /* ------------------------------ localizar ------------------------------- */

  hit(p: Pt, type: string): number {
    const w = toWorld(this.frame, this.view, p.x, p.y);
    const sc = scaleOf(this.frame, this.view);
    for (let zi = 0; zi < this.m.zones.length; zi++) {
      const z = this.m.zones[zi]!;
      if (w.x >= z.x && w.x <= z.x + z.w && w.y >= z.y && w.y <= z.y + z.h) return this.seatCount + zi;
    }
    // Zona táctil efectiva: ~15 px de radio con el dedo (≥ 24 px de diámetro a zoom moderado) y el círculo +2 px con ratón.
    const maxD = type === "mouse" || type === "pen" ? this.m.radius * 1.1 + 2 / sc : Math.max(this.m.radius * 1.3, 15 / sc);
    return this.grid.nearest(w.x, w.y, maxD);
  }

  /* ------------------------------ eventos --------------------------------- */

  private pos(e: { clientX: number; clientY: number }): Pt {
    const r = this.el.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  private bind(): void {
    const el = this.el;
    el.addEventListener("pointerdown", this.onDown);
    el.addEventListener("pointermove", this.onMove);
    el.addEventListener("pointerup", this.onUp);
    el.addEventListener("pointercancel", this.onCancel);
    el.addEventListener("pointerleave", this.onLeave);
    el.addEventListener("wheel", this.onWheel, { passive: false });
    this.svg.addEventListener("keydown", this.onKey);
    this.svg.addEventListener("focusin", this.onFocusIn);
    this.svg.addEventListener("focusout", this.onFocusOut);
    // Activación sintética (lectores de pantalla): `click` sin puntero real
    this.svg.addEventListener("click", this.onClick);
  }

  private onDown = (e: PointerEvent): void => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    this.kbd = false;
    const p = this.pos(e);
    this.pointers.set(e.pointerId, p);
    cancelAnimationFrame(this.anim);
    this.o.cb.hover(-1, 0, 0);
    try {
      this.el.setPointerCapture(e.pointerId);
    } catch {
      /* puntero ya liberado */
    }
    if (this.pointers.size === 1) {
      this.down = { id: e.pointerId, x: p.x, y: p.y, view: this.view, moved: false, type: e.pointerType };
    } else if (this.pointers.size === 2) {
      const pts = [...this.pointers.values()];
      this.pinch = { a0: pts[0]!, b0: pts[1]!, view: this.view };
      this.down = null;
    }
  };

  private onMove = (e: PointerEvent): void => {
    const p = this.pos(e);
    if (!this.pointers.has(e.pointerId)) {
      if (e.pointerType === "mouse") this.queueHover(p);
      return;
    }
    this.pointers.set(e.pointerId, p);
    if (this.pinch && this.pointers.size >= 2) {
      const pts = [...this.pointers.values()];
      this.setView(pinchView(this.frame, this.pinch.view, this.pinch.a0, this.pinch.b0, pts[0]!, pts[1]!));
      return;
    }
    const d = this.down;
    if (d && d.id === e.pointerId) {
      const dx = p.x - d.x;
      const dy = p.y - d.y;
      if (!d.moved && Math.hypot(dx, dy) > TAP_SLOP) {
        d.moved = true;
        if (this.zoomed) this.el.classList.add("g");
      }
      if (d.moved && this.zoomed) this.setView(panBy(this.frame, d.view, dx, dy));
    }
  };

  private onUp = (e: PointerEvent): void => {
    const p = this.pos(e);
    this.pointers.delete(e.pointerId);
    this.el.classList.remove("g");
    if (this.pinch) {
      if (this.pointers.size < 2) this.pinch = null;
      this.down = null;
      return;
    }
    const d = this.down;
    this.down = null;
    this.lastPointerUp = e.timeStamp;
    if (!d || d.id !== e.pointerId || d.moved) return;
    this.tap(p, d.type);
  };

  private onCancel = (e: PointerEvent): void => {
    this.pointers.delete(e.pointerId);
    this.el.classList.remove("g");
    if (this.pointers.size < 2) this.pinch = null;
    this.down = null;
  };

  private onLeave = (e: PointerEvent): void => {
    if (e.pointerType === "mouse" && !this.pointers.size) {
      this.lastHover = -2;
      this.o.cb.hover(-1, 0, 0);
    }
  };

  private onWheel = (e: WheelEvent): void => {
    if (!(e.ctrlKey || e.metaKey)) {
      this.o.cb.wheelHint(); // sin Ctrl/⌘ no se secuestra el scroll de la página
      return;
    }
    e.preventDefault();
    const p = this.pos(e);
    this.setView(zoomAt(this.frame, this.view, p.x, p.y, this.view.k * wheelFactor(e.deltaY, e.deltaMode)));
  };

  private tap(p: Pt, type: string): void {
    const node = this.hit(p, type);
    const now = performance.now();
    if (node >= 0) {
      this.lastTap = null;
      const sc = scaleOf(this.frame, this.view);
      // Con el dedo y butacas demasiado juntas en pantalla: primero se acerca, luego se elige.
      if (type === "touch" && node < this.seatCount && this.m.pitch * sc < MIN_TOUCH_PITCH && !this.atMax) {
        const w = toWorld(this.frame, this.view, p.x, p.y);
        const k = kForPitch(this.frame, this.m.pitch, COMFORT_PITCH);
        this.animateTo(centerOn(this.frame, w.x, w.y, k));
        return;
      }
      this.o.cb.activate(node, "pointer");
      this.focusNode(node, false, false); // el foco sigue a la selección, sin anillo, para continuar con el teclado
      return;
    }
    this.o.cb.backgroundTap();
    const last = this.lastTap;
    if (last && now - last.t < DBL_MS && Math.hypot(p.x - last.x, p.y - last.y) < 28) {
      this.lastTap = null;
      this.zoomBy(this.atMax ? 1 / this.view.k : 2.2, p);
    } else this.lastTap = { t: now, x: p.x, y: p.y };
  }

  private queueHover(p: Pt): void {
    this.hoverPt = p;
    if (this.hoverRaf) return;
    this.hoverRaf = requestAnimationFrame(() => {
      this.hoverRaf = 0;
      const pt = this.hoverPt;
      if (!pt || this.down) return;
      const n = this.hit(pt, "mouse");
      if (n !== this.lastHover || n >= 0) {
        this.lastHover = n;
        this.o.cb.hover(n, pt.x, pt.y);
      }
    });
  }

  private onClick = (e: MouseEvent): void => {
    if (e.timeStamp - this.lastPointerUp < 500) return; // un clic real ya se atendió en pointerup
    const n = this.nodeOf(e.target);
    if (n >= 0) this.o.cb.activate(n, "ax");
  };

  private onFocusIn = (e: FocusEvent): void => {
    const n = this.nodeOf(e.target);
    if (n < 0) return;
    if (n !== this.current) this.setCurrent(n);
    if (this.kbd) this.placeRing(n);
    this.o.cb.focus(n, this.kbd);
  };

  private onFocusOut = (): void => {
    this.hideRing();
    this.o.cb.blur();
  };

  private onKey = (e: KeyboardEvent): void => {
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    this.kbd = true;
    const n = this.nodeOf(e.target);
    const dir = KEY_DIR[e.key];
    if (dir && n >= 0) {
      e.preventDefault();
      const next = navigate(this.nav, n, dir);
      if (next >= 0) this.focusNode(next, true);
      return;
    }
    switch (e.key) {
      case "Enter":
      case " ":
        if (n >= 0) {
          e.preventDefault();
          this.o.cb.activate(n, "key");
        }
        return;
      case "Escape":
        if (this.o.cb.escape()) e.preventDefault();
        return;
      case "Home":
      case "End": {
        if (n < 0 || n >= this.seatCount) return;
        const row = this.m.rows[this.m.seats[n]!.row]!;
        e.preventDefault();
        this.focusNode(e.key === "Home" ? row.seats[0]! : row.seats[row.seats.length - 1]!, true);
        return;
      }
      case "+":
      case "=":
        e.preventDefault();
        this.zoomBy(1.6);
        return;
      case "-":
      case "_":
        e.preventDefault();
        this.zoomBy(1 / 1.6);
        return;
      case "0":
        e.preventDefault();
        this.reset();
        return;
    }
  };

  destroy(): void {
    cancelAnimationFrame(this.anim);
    cancelAnimationFrame(this.hoverRaf);
  }
}
